import { FC, useState } from "react";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { ModalTitle, ModalActions, Button } from "@kaltura/ds-react-components";
import { StyledModalContent, StyledActions, StyledActionButtons } from "./StyleComponents";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import AddFromUrlImportForm from "./AddFromUrlImportForm";
import { AddFromUrlConfig, AddFromUrlImportModalState } from "./types";

export interface AddFromUrlImportModalProps {
    open: boolean;
    config: AddFromUrlConfig;
    onCancel?: () => void;
    initialState?: AddFromUrlImportModalState;
    showTitle?: boolean;
    className?: string;
    allowedExtensions?: string[];
}

/*
 * AddFromUrlImportModal is a modal dialog wrapper component for importing media from a URL.
 * It provides the modal title and action buttons, while delegating the import logic to AddFromUrlImportForm.
 * The modal acts as a container that can be reused with different form implementations.
 */
export const AddFromUrlImportModal: FC<AddFromUrlImportModalProps> = ({
    onCancel,
    initialState,
    showTitle = true,
    className,
    config,
    allowedExtensions,
}) => {
    const rootClassName = "add-from-url-import-modal";
    const [formData, setFormData] = useState<{
        uploadUrl: string | null;
        isLoading: boolean;
        isImporting: boolean;
        error?: string;
    }>({
        uploadUrl: null,
        isLoading: false,
        isImporting: false,
        error: undefined,
    });

    const formId = `${rootClassName}-form`; // Create unique form ID
    const { showToast } = useToastsContext();
    const handleError = (message: string) => {
        showToast({ severity: "error", message });
    };

    return (
        <>
            {showTitle && (
                <ModalTitle data-testid={rootClassName + "-main-title"}>{translate("Import from URL")}</ModalTitle>
            )}
            <StyledModalContent className={`${className ?? ""} ${rootClassName}-content`}>
                <AddFromUrlImportForm
                    config={config}
                    initialState={initialState}
                    rootClassName={rootClassName}
                    onDataChange={setFormData}
                    formId={formId}
                    allowedExtensions={allowedExtensions}
                    onError={handleError}
                />
            </StyledModalContent>
            <ModalActions className={`${rootClassName}-actions`}>
                <StyledActions>
                    <StyledActionButtons>
                        {!!onCancel && (
                            <Button
                                kuid={rootClassName + "-button-cancel"}
                                variant="borderless"
                                onClick={onCancel}
                                color={"secondary"}
                            >
                                {translate("Cancel")}
                            </Button>
                        )}
                        {(() => {
                            const isDisabled = !!formData.error || formData.isLoading || formData.isImporting;
                            return (
                                <Button
                                    variant="pill"
                                    type="submit"
                                    form={formId}
                                    disabled={isDisabled}
                                    loading={formData.isImporting}
                                    kuid={`${rootClassName}-button-import`}
                                >
                                    {translate("Import")}
                                </Button>
                            );
                        })()}
                    </StyledActionButtons>
                </StyledActions>
            </ModalActions>
        </>
    );
};

export default AddFromUrlImportModal;
